import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FaVideo } from 'react-icons/fa';
import { getTicketVideos, uploadTicketVideo } from '../../services/ticketService';
import { useApp } from '../../context/AppContext';

const MAX_VIDEO_BYTES = 50 * 1024 * 1024;
const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.m4v', '.ogg'];

const isVideoFile = (file) => {
  if (!file) return false;
  if (file.type && file.type.startsWith('video/')) return true;
  const name = String(file.name || '').toLowerCase();
  return VIDEO_EXTENSIONS.some((ext) => name.endsWith(ext));
};

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;
  padding: 1rem;
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border-primary);
  border-radius: 12px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;

  h3,
  h4 {
    margin: 0;
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    font-size: 1rem;
    font-weight: 600;
    color: var(--color-text-primary);
  }
`;

const UploadButton = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 0.75rem;
  border-radius: 8px;
  border: 1px solid var(--color-border-primary);
  background: var(--color-bg-tertiary);
  color: var(--color-text-primary);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};

  input {
    display: none;
  }
`;

const VideosList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

const VideoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 0;
`;

const VideoPlayer = styled.video`
  width: 100%;
  max-width: 520px;
  max-height: 320px;
  border-radius: 10px;
  background: #000;
  border: 1px solid var(--color-border-primary);

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const VideoMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  max-width: 520px;

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const VideoName = styled.span`
  font-size: 0.82rem;
  color: var(--color-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const OpenLink = styled.a`
  flex-shrink: 0;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--color-text-primary);
  text-decoration: underline;
`;

const EmptyHint = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: var(--color-text-secondary);
`;

const TicketVideosSection = ({
  ticketId,
  refreshKey = 0,
  allowUpload = true,
  title = 'Vídeos',
  compactTitle = false,
  onCountChange,
}) => {
  const { addToast } = useApp();
  const inputRef = useRef(null);
  const onCountChangeRef = useRef(onCountChange);
  onCountChangeRef.current = onCountChange;
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const fetchVideos = useCallback(async () => {
    if (!ticketId) return;
    try {
      setLoading(true);
      const response = await getTicketVideos(ticketId);
      const list = Array.isArray(response) ? response : [];
      setVideos(list);
      onCountChangeRef.current?.(list.length);
    } catch (err) {
      console.error('Erro ao carregar vídeos do ticket:', err);
      setVideos([]);
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos, refreshKey]);

  const handleFilesSelected = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!ticketId || files.length === 0) return;

    const videosToUpload = files.filter(isVideoFile);
    if (videosToUpload.length === 0) {
      addToast?.('Selecione um arquivo de vídeo.', 'error');
      return;
    }

    const oversized = videosToUpload.filter((file) => file.size > MAX_VIDEO_BYTES);
    if (oversized.length > 0) {
      addToast?.(
        'Cada vídeo deve ter no máximo 50 MB. Remova os arquivos maiores e tente de novo.',
        'error'
      );
    }

    const accepted = videosToUpload.filter((file) => file.size <= MAX_VIDEO_BYTES);
    if (accepted.length === 0) return;

    try {
      setUploading(true);
      await Promise.all(accepted.map((file) => uploadTicketVideo(ticketId, file)));
      await fetchVideos();
      addToast?.(
        accepted.length === 1 ? 'Vídeo enviado.' : `${accepted.length} vídeos enviados.`,
        'success'
      );
    } catch (err) {
      console.error('Erro ao enviar vídeo:', err);
      addToast?.(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          (err?.response?.status
            ? `Falha ao enviar vídeo (${err.response.status})`
            : 'Falha ao enviar o vídeo.'),
        'error'
      );
    } finally {
      setUploading(false);
    }
  };

  if (!ticketId) return null;

  const TitleTag = compactTitle ? 'h4' : 'h3';

  return (
    <Section aria-label="Vídeos do ticket">
      <SectionHeader>
        <TitleTag>
          <FaVideo aria-hidden />
          {title}
          {videos.length > 0 ? ` (${videos.length})` : ''}
        </TitleTag>
        {allowUpload && (
          <UploadButton $disabled={uploading}>
            <input
              ref={inputRef}
              type="file"
              accept="video/*,.mp4,.webm,.mov,.m4v"
              multiple
              disabled={uploading}
              onChange={handleFilesSelected}
            />
            {uploading ? 'Enviando...' : 'Anexar vídeo'}
          </UploadButton>
        )}
      </SectionHeader>

      {loading && videos.length === 0 ? (
        <EmptyHint>Carregando vídeos...</EmptyHint>
      ) : videos.length === 0 ? (
        <EmptyHint>Nenhum vídeo anexado a este ticket.</EmptyHint>
      ) : (
        <VideosList>
          {videos.map((video, index) => (
            <VideoItem key={video.url || video.name || index}>
              <VideoPlayer controls preload="metadata" src={video.url}>
                Seu navegador não suporta a reprodução deste vídeo.
              </VideoPlayer>
              <VideoMeta>
                <VideoName>{video.name || 'Vídeo'}</VideoName>
                {video.url && (
                  <OpenLink href={video.url} target="_blank" rel="noopener noreferrer">
                    Abrir
                  </OpenLink>
                )}
              </VideoMeta>
            </VideoItem>
          ))}
        </VideosList>
      )}
    </Section>
  );
};

TicketVideosSection.propTypes = {
  ticketId: PropTypes.string,
  refreshKey: PropTypes.number,
  allowUpload: PropTypes.bool,
  title: PropTypes.string,
  compactTitle: PropTypes.bool,
  onCountChange: PropTypes.func,
};

export default TicketVideosSection;
